/* eslint-disable no-console */
/* eslint-disable consistent-return, no-return-assign */
import detectEthereumProvider from '@metamask/detect-provider'
import {
  Actions,
  AddEthereumChainParameter,
  Provider,
  ProviderConnectInfo,
  ProviderRpcError,
  WatchAssetParameters,
  Connector,
} from '@web3-react/types'

type MetaMaskProvider = Provider & {
  isMetaMask?: boolean
  isOkxWallet?: boolean
  isTrustWallet?: boolean
  isTrust?: boolean
  isBitKeep?: boolean
  isBlockWallet?: boolean
  isCypherD?: boolean
  isBraveWallet?: boolean
  isPhantom?: boolean
  providerInfo?: { name?: string }
  isConnected?: () => boolean
  detected?: MetaMaskProvider[]
  providers?: MetaMaskProvider[]
  chainId?: string
}

export class NoMetaMaskError extends Error {
  public constructor() {
    super('MetaMask not installed')
    this.name = NoMetaMaskError.name
    Object.setPrototypeOf(this, NoMetaMaskError.prototype)
  }
}

function parseChainId(chainId: string) {
  return Number.parseInt(chainId, 16)
}

function isStrictMetaMaskProvider(provider?: MetaMaskProvider) {
  if (!provider?.isMetaMask) {
    return false
  }

  if (
    provider.isOkxWallet ||
    provider.isTrustWallet ||
    provider.isTrust ||
    provider.isBitKeep ||
    provider.isBlockWallet ||
    provider.isCypherD ||
    provider.isBraveWallet ||
    provider.isPhantom
  ) {
    return false
  }

  const providerName = provider.providerInfo?.name?.toLowerCase?.() ?? ''
  if (providerName && !providerName.includes('metamask')) {
    return false
  }

  return true
}

/**
 * @param options - Options to pass to `@metamask/detect-provider`
 * @param onError - Handler to report errors thrown from eventListeners.
 */
export interface MetaMaskConstructorArgs {
  actions: Actions
  options?: Parameters<typeof detectEthereumProvider>[0]
  onError?: (error: Error) => void
}

export class MetaMask extends Connector {
  /** {@inheritdoc Connector.provider} */
  public provider?: MetaMaskProvider

  private readonly options?: Parameters<typeof detectEthereumProvider>[0]

  private eagerConnection?: Promise<void>

  constructor({ actions, options, onError }: MetaMaskConstructorArgs) {
    super(actions, onError)
    this.options = options
  }

  private providers: MetaMaskProvider[] = []
  private providerMap = new Map<string, MetaMaskProvider>()

  private async isomorphicInitialize(): Promise<void> {
    if (this.eagerConnection) return

    return (this.eagerConnection = Promise.resolve().then(async () => {
      // Clear previous state for fresh detection
      this.providers = []
      this.providerMap.clear()

      // 1. Listen for EIP-6963 announcements
      const handleAnnounceProvider = (event: CustomEvent) => {
        const providerDetail = event.detail
        const provider = providerDetail.provider as MetaMaskProvider

        if (isStrictMetaMaskProvider(provider)) {
          this.providers.push(provider)
          this.providerMap.set(providerDetail.info.uuid, provider)
        }
      }

      window.addEventListener('eip6963:announceProvider', handleAnnounceProvider as any)

      // 2. Request all providers
      window.dispatchEvent(new Event('eip6963:requestProvider'))

      // 3. Fallback to legacy window.ethereum
      const legacyProvider = window.ethereum as MetaMaskProvider
      if (legacyProvider && isStrictMetaMaskProvider(legacyProvider)) {
        this.providers.push(legacyProvider)
      }

      // Wait briefly for announcements (non-blocking)
      await new Promise((resolve) => setTimeout(resolve, 500))

      window.removeEventListener('eip6963:announceProvider', handleAnnounceProvider as any)

      // Select first valid MetaMask provider
      this.provider = this.providers.find((p) => isStrictMetaMaskProvider(p)) || undefined

      if (!this.provider) return

      // Your existing event listeners (unchanged)
      this.provider.on('connect', ({ chainId }: ProviderConnectInfo): void => {
        this.actions.update({ chainId: parseChainId(chainId) })
      })

      this.provider.on('disconnect', (error: ProviderRpcError): void => {
        if (error?.code === 1013) {
          console.debug('MetaMask logged connection error 1013: "Try again later"')
          return
        }
        this.actions.resetState()
        this.onError?.(error)
      })

      this.provider.on('chainChanged', (chainId: string): void => {
        this.actions.update({ chainId: parseChainId(chainId) })
      })

      this.provider.on('accountsChanged', (accounts: string[]): void => {
        if (accounts.length === 0) {
          this.actions.resetState()
        } else {
          this.actions.update({ accounts })
        }
      })
    }))
  }

  /** {@inheritdoc Connector.connectEagerly} */
  public async connectEagerly(): Promise<void> {
    const cancelActivation = this.actions.startActivation()

    try {
      await this.isomorphicInitialize()
      if (!this.provider) return cancelActivation()

      // Wallets may resolve eth_chainId and hang on eth_accounts pending user interaction, which may include changing
      // chains; they should be requested serially, with accounts first, so that the chainId can settle.
      const accounts = (await this.provider.request({
        method: 'eth_requestAccounts',
      })) as string[]
      if (!accounts.length) throw new Error('No accounts returned')
      const chainId = (await this.provider.request({
        method: 'eth_chainId',
      })) as string
      this.actions.update({ chainId: parseChainId(chainId), accounts })
    } catch (error) {
      console.debug('Could not connect eagerly', error)
      // we should be able to use `cancelActivation` here, but on mobile, metamask emits a 'connect'
      // event, meaning that chainId is updated, and cancelActivation doesn't work because an intermediary
      // update has occurred, so we reset state instead
      this.actions.resetState()
    }
  }

  /**
   * Initiates a connection.
   *
   * @param desiredChainIdOrChainParameters - If defined, indicates the desired chain to connect to. If the user is
   * already connected to this chain, no additional steps will be taken. Otherwise, the user will be prompted to switch
   * to the chain, if one of two conditions is met: either they already have it added in their extension, or the
   * argument is of type AddEthereumChainParameter, in which case the user will be prompted to add the chain with the
   * specified parameters first, before being prompted to switch.
   */
  public async activate(desiredChainIdOrChainParameters?: number | AddEthereumChainParameter): Promise<void> {
    let cancelActivation: () => void
    if (!this.provider?.isConnected?.()) cancelActivation = this.actions.startActivation()

    // Always re-detect provider when user explicitly selects this wallet (avoid reusing previous wallet).
    this.eagerConnection = undefined
    this.provider = undefined

    return this.isomorphicInitialize()
      .then(async () => {
        if (!this.provider) throw new NoMetaMaskError()

        // Wallets may resolve eth_chainId and hang on eth_accounts pending user interaction, which may include changing
        // chains; they should be requested serially, with accounts first, so that the chainId can settle.
        const accounts = (await this.provider.request({
          method: 'eth_requestAccounts',
        })) as string[]
        const chainId = (await this.provider.request({
          method: 'eth_chainId',
        })) as string
        const receivedChainId = parseChainId(chainId)
        const desiredChainId =
          typeof desiredChainIdOrChainParameters === 'number'
            ? desiredChainIdOrChainParameters
            : desiredChainIdOrChainParameters?.chainId

        // if there's no desired chain, or it's equal to the received, update
        if (!desiredChainId || receivedChainId === desiredChainId)
          return this.actions.update({ chainId: receivedChainId, accounts })

        const desiredChainIdHex = `0x${desiredChainId.toString(16)}`
        const chainParams = {
          chainId: desiredChainIdHex,
          chainName: 'Ethereum Mainnet',
          rpcUrls: ['https://ethereum-rpc.publicnode.com'],
          nativeCurrency: {
            name: 'Ether',
            symbol: 'ETH',
            decimals: 18,
          },
          blockExplorerUrls: ['https://etherscan.io'],
        }

        const networkConflictError = new Error(
          'Please remove the old network that uses this RPC URL from MetaMask (Settings → Networks) and reconnect.',
        )

        // Try switching first, then add if chain is unknown
        return this.provider
          .request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: desiredChainIdHex }],
          })
          .catch(async (error: ProviderRpcError) => {
            const errorCode = (error.data as any)?.originalError?.code || error.code

            // 4902: chain not in MetaMask yet — try adding it
            if (errorCode === 4902) {
              if (!this.provider) throw new Error('No provider')
              try {
                return await this.provider.request({
                  method: 'wallet_addEthereumChain',
                  params: [chainParams],
                })
              } catch (addError: any) {
                // RPC URL already used by another chain ID (old Supra Testnet)
                const addErrorCode = addError?.data?.originalError?.code || addError?.code
                if (addErrorCode === -32603) {
                  throw networkConflictError
                }
                throw addError
              }
            }

            // -32603: RPC endpoint conflict with existing network
            if (errorCode === -32603) {
              throw networkConflictError
            }

            throw error
          })
          .then(() => this.activate(desiredChainId))
      })
      .catch((error) => {
        cancelActivation?.()
        throw error
      })
  }

  public async watchAsset({ address, symbol, decimals, image }: WatchAssetParameters): Promise<true> {
    if (!this.provider) throw new Error('No provider')

    return this.provider
      .request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20', // Initially only supports ERC20, but eventually more!
          options: {
            address, // The address that the token is at.
            symbol, // A ticker symbol or shorthand, up to 5 chars.
            decimals, // The number of decimals in the token
            image, // A string url of the token logo
          },
        },
      })
      .then((success) => {
        if (!success) throw new Error('Rejected')
        return true
      })
  }
}
